import Button from 'tio-reimagined/components/button';
import { concat, hash } from '@ember/helper';
import { not } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import Popover from 'tio-reimagined/components/popover';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Textarea from 'tio-reimagined/components/textarea';
import Component from '@glimmer/component';
import ErrorMessages from 'tio-reimagined/components/error-messages';
import type Intl from 'ember-intl/services/intl';
import { PhPencil, PhX, PhCheck, PhArrowUUpLeft } from 'ember-phosphor-icons';

export interface EditPopoverSignature {
  Args: {
    /**
     * Title displayed in the popover header
     */
    title: string;
    /**
     * Whether the async task is currently running
     */
    isRunning?: boolean;
    /**
     * Whether the form is valid and can be submitted
     */
    isValid?: boolean;
    /**
     * Error message to display
     */
    error?: string;
    /**
     * Icon to display on the trigger button (defaults to pencil)
     */
    icon?: 'pencil' | 'revert';
    /**
     * Size of the popover content
     */
    size?: 'sm' | 'md' | 'lg' | 'xl';
    /**
     * Submit button label (defaults to "Save")
     */
    submitLabel?: string;
    /**
     * Callback when the form is submitted. Receives the reason for edit.
     */
    onSubmit: (reasonForEdit: string) => void | Promise<void>;
  };
  Blocks: {
    /**
     * Content to render inside the popover form (before the reason textarea).
     * The reasonForEdit value and updateReasonForEdit callback are yielded
     * for display purposes (e.g., showing the current reason in a preview).
     */
    default: [{ reasonForEdit: string; updateReasonForEdit: (reason: string) => void }];
    /**
     * Optional description text below the title
     */
    description?: [];
  };
  Element: HTMLDivElement;
}

/**
 * A reusable popover component for edit forms that require a reason/comment.
 * Provides consistent UI patterns for edit, save, cancel, and error handling.
 *
 * The onSubmit callback receives the reason for edit as a parameter.
 *
 * @example
 * ```gts
 * <EditPopover
 *   @title="Edit Field"
 *   @isRunning={{this.saveTask.isRunning}}
 *   @isValid={{this.isFormValid}}
 *   @error={{this.errorMessage}}
 *   @onSubmit={{this.handleSubmit}}
 * >
 *   <:description>
 *     <span class="text-gray-500 text-sm">Some helpful description</span>
 *   </:description>
 *   <:default as |popover|>
 *     <Input
 *       @value={{this.editableValue}}
 *       @onChange={{this.setValue}}
 *     />
 *     {{! popover.reasonForEdit is available if needed for display }}
 *   </:default>
 * </EditPopover>
 *
 * // In your component:
 * handleSubmit = async (reasonForEdit: string) => {
 *   await this.saveTask.perform(reasonForEdit);
 * };
 * ```
 */
export default class EditPopover extends Component<EditPopoverSignature> {
  @tracked isOpen = false;
  @tracked reasonForEdit = '';
  @tracked didSubmit = false;
  @tracked internalError?: string;

  @service declare intl: Intl;

  get isValid() {
    // Reason is always required; additional validation comes from args
    return !!this.reasonForEdit && (this.args.isValid ?? true);
  }

  get size() {
    return this.args.size ?? 'xl';
  }

  get submitLabel() {
    return this.args.submitLabel ?? this.intl.t('common.save');
  }

  toggleOpen = () => {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.reasonForEdit = '';
      this.didSubmit = false;
      this.internalError = undefined;
    }
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  cancel = () => {
    this.didSubmit = false;
    this.internalError = undefined;
    this.toggleOpen();
  };

  handleSubmit = async (event: Event) => {
    event.preventDefault();
    this.didSubmit = true;
    this.internalError = undefined;
    try {
      await this.args.onSubmit(this.reasonForEdit);
      // If successful and no external error, close the popover
      if (!this.args.error) {
        this.toggleOpen();
      }
    } catch (e) {
      // Catch thrown errors and display them
      this.internalError = e instanceof Error ? e.message : String(e);
    }
  };

  get displayError() {
    return this.args.error ?? this.internalError;
  }

  get TriggerIcon() {
    return this.args.icon === 'revert' ? PhArrowUUpLeft : PhPencil;
  }

  get triggerTitle() {
    return this.args.icon === 'revert' ? this.intl.t('common.revert') : this.intl.t('common.edit');
  }

  <template>
    <Popover
      @isOpen={{this.isOpen}}
      @onOpen={{this.toggleOpen}}
      @onClose={{this.cancel}}
      ...attributes
    >
      <:trigger as |pop|>
        <Button
          @appearance="minimal"
          @intent="primary"
          title={{this.triggerTitle}}
          aria-label={{this.triggerTitle}}
          {{pop.register}}
          {{on "click" this.toggleOpen}}
          data-test-edit-popover-trigger
        >
          <this.TriggerIcon @size="16" />
        </Button>
      </:trigger>
      <:body>
        <form class="flex flex-col gap-4 p-4" {{on "submit" this.handleSubmit}}>
          <h2 class="text-xl font-semibold">{{@title}}</h2>

          {{#if (has-block "description")}}
            {{yield to="description"}}
          {{/if}}

          {{yield
            (hash reasonForEdit=this.reasonForEdit updateReasonForEdit=this.updateReasonForEdit)
          }}

          <Textarea
            data-test-reason-for-edit
            disabled={{@isRunning}}
            @value={{this.reasonForEdit}}
            @onChange={{this.updateReasonForEdit}}
            @label={{concat (t "common.comment") "*"}}
            @classes={{hash base="text-xs"}}
          />

          <div class="flex gap-2 justify-end">
            <Button
              type="button"
              data-test-edit-popover-cancel
              class="w-auto"
              @intent="danger"
              {{on "click" this.cancel}}
              disabled={{@isRunning}}
              title={{t "common.cancel"}}
              aria-label={{t "common.cancel"}}
            >
              <PhX @size="16" />
            </Button>
            <Button
              type="submit"
              data-test-edit-popover-save
              class="w-auto"
              @intent="primary"
              @isRunning={{@isRunning}}
              disabled={{not this.isValid}}
              title={{this.submitLabel}}
              aria-label={{this.submitLabel}}
            >
              <PhCheck @size="16" />
            </Button>
          </div>

          <ErrorMessages
            @showErrors={{this.didSubmit}}
            @error={{this.displayError}}
            class="text-xs"
          />
        </form>
      </:body>
    </Popover>
  </template>
}
